const roomKey = item => String(typeof item === 'object' ? item?.room || '' : item || '').trim().toUpperCase();
const active = stay => String(stay?.status || '').trim().toLowerCase() === 'checked_in' &&
  stay?.checkedOut !== true && stay?.checkoutCompleted !== true;
const isQuick = card => card?.detailsPending === true || card?.status === 'details_pending';
const quickIdentity = card => JSON.stringify([card?.quickOccupancyId || '', card?.occupiedAt || card?.checkInAt || '',
  card?.temporaryGuestName || card?.guestName || '']);

// Capture once when the form opens, never from live state when Save is clicked.
export function captureCheckInContext(room, card) {
  return {room: roomKey(room), mode: isQuick(card) ? 'quick' : 'ready',
    quickOccupancyId: card?.quickOccupancyId || '', quickIdentity: quickIdentity(card)};
}

export function assertCheckInContext(board, lock, expected, room) {
  room = roomKey(room);
  if (!expected || expected.room !== room) throw new Error('Check-in form ka room badal gaya hai. Fresh form kholein.');
  const card = (board.occupiedRooms || []).find(item => roomKey(item) === room);
  if (expected.mode === 'quick') {
    if (!isQuick(card) || lock?.activeStayId || lock?.status !== 'details_pending' ||
        quickIdentity(card) !== expected.quickIdentity ||
        String(lock.quickOccupancyId || '') !== String(expected.quickOccupancyId || '')) {
      throw new Error(`Room ${room}: Name Only guest entry badal gayi hai. Purana form save nahi hua; fresh Add Details kholein.`);
    }
  } else if (card || lock?.status === 'details_pending') {
    throw new Error(`Room ${room}: doosre manager ne guest entry kar di hai. Fresh board se Add Details/Manage kholein.`);
  }
}

export function assertNoActiveRoomStays(stays, hotelId, room) {
  const conflicts = (stays || []).filter(stay => stay.hotelId === hotelId && roomKey(stay) === roomKey(room) && active(stay));
  if (conflicts.length) {
    const guests = conflicts.map(stay => `${stay.guestName || 'Guest'} (${stay.stayId || stay.id || 'existing entry'})`).join(', ');
    throw new Error(`Room ${roomKey(room)}: existing active entry mili — ${guests}. Pehle existing stay verify/restore karein; duplicate check-in nahi hui.`);
  }
}

// Presentation only. Never manufacture a Ready room or write derived cards back.
// Board and stay listeners can deliver the same transaction in either order.
export function buildRoomDisplayRows(board = {}, stays = new Map()) {
  const activeByRoom = new Map();
  for (const [id, stay] of stays) {
    if (!active(stay)) continue;
    const room = roomKey(stay);
    activeByRoom.set(room, [...(activeByRoom.get(room) || []), {...stay, stayId: id}]);
  }
  const rows = new Map();
  const pending = (card, reason) => ({room: roomKey(card), category: card.category || card.roomCategory || '',
    roomCategory: card.roomCategory || card.category || '', pmsStatus: 'sync_pending', syncReason: reason});
  for (const [list, status] of [['vacantRooms', 'vacant'], ['occupiedRooms', 'occupied'], ['checkoutRooms', 'checkout']]) {
    for (const item of board[list] || []) {
      const card = typeof item === 'object' ? item : {room: item};
      const room = roomKey(card); if (!room) continue;
      const known = activeByRoom.get(room) || [];
      const stayId = String(card.stayId || card.id || '');
      const fresh = stays.get(stayId);
      let row = {...card, room, pmsStatus: status};
      if (status === 'occupied') {
        if (isQuick(card)) row.pmsStatus = 'details_pending';
        if (fresh && (!active(fresh) || roomKey(fresh) !== room)) {
          const cardShift = Date.parse(card.lastRoomShiftAt || ''), stayShift = Date.parse(fresh.lastRoomShiftAt || '');
          const boardArrivedFirst = active(fresh) && card.previousRoom && roomKey(card.previousRoom) === roomKey(fresh) &&
            Number.isFinite(cardShift) && (!Number.isFinite(stayShift) || cardShift > stayShift);
          if (!boardArrivedFirst) row = pending(card, 'Guest aur room status update ho raha hai. Refresh status karein.');
        } else if (fresh) row = {...card, ...fresh, room, stayId, pmsStatus: 'occupied'};
        if (known.some(stay => stay.stayId !== stayId)) row = pending(card, 'Room mein conflicting active entry hai. Existing guest verify karein.');
      } else if (status === 'vacant' && known.length) {
        row = pending(card, 'Guest entry mili hai; room ko Ready assume nahi kar sakte. Refresh status karein.');
      }
      if (rows.has(room)) row = pending(card, 'Room ki do status cards mili hain. Status verify karein.');
      rows.set(room, row);
    }
  }
  for (const [room, entries] of activeByRoom) {
    if (room && !rows.has(room)) rows.set(room, pending(entries[0], 'Active guest ki board card missing hai. Room Setup se existing room restore karein.'));
  }
  return [...rows.values()];
}
